package com.rajen.angrejidictionary.pages.admin

import androidx.compose.runtime.*
import com.rajen.angrejidictionary.data.remote.dto.SuggestionItem
import com.rajen.angrejidictionary.presentation.viewmodel.SuggestionsViewModel
import com.rajen.angrejidictionary.util.writeClipboardText
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

val SuggestionCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Color.rgb(0xFF, 0xF7, 0xF1))
        .borderRadius(16.px)
        .padding(1.25.cssRem)
        .border(1.px, LineStyle.Solid, Color.rgb(0xE9, 0xCC, 0xB7))
}

@Composable
fun SuggestionsTab(viewModel: SuggestionsViewModel) {
    Column(
        Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(1.cssRem)
    ) {
        // Header
        // ✅ FIX 1: Remove .alignItems() from Modifier chain
        Row(
            Modifier.fillMaxWidth()
                .justifyContent(JustifyContent.SpaceBetween)
                .flexWrap(FlexWrap.Wrap)
                .gap(0.75.cssRem),
            verticalAlignment = Alignment.CenterVertically  // ✅ Use parameter instead
        ) {
            Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "Word Suggestions (${viewModel.totalSuggestions})",
                    Modifier.fontSize(1.2.cssRem).fontWeight(FontWeight.Bold)
                        .color(Color.rgb(0x21, 0x00, 0x00))
                )
                RefreshButton(isLoading = viewModel.isLoading) { viewModel.refresh() }
            }

            // Status Filter
            Row(Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap)) {
                FilterButton("All", viewModel.selectedStatus == null) {
                    viewModel.loadSuggestions(1, null)
                }
                FilterButton("Pending", viewModel.selectedStatus == "pending") {
                    viewModel.loadSuggestions(1, "pending")
                }
                FilterButton("Approved", viewModel.selectedStatus == "approved") {
                    viewModel.loadSuggestions(1, "approved")
                }
                FilterButton("Rejected", viewModel.selectedStatus == "rejected") {
                    viewModel.loadSuggestions(1, "rejected")
                }
            }
        }

        // Action message
        viewModel.actionMessage?.let { msg ->
            Div(
                Modifier.fillMaxWidth()
                    .backgroundColor(Color.rgb(0xE8, 0xF5, 0xE9))
                    .borderRadius(8.px)
                    .padding(0.75.cssRem)
                    .color(Color.rgb(0x2E, 0x7D, 0x32))
                    .toAttrs()  // ✅ FIX 2: Add .toAttrs()
            ) {
                Text(msg)
            }
            LaunchedEffect(msg) {
                kotlinx.coroutines.delay(3000)
                viewModel.clearActionMessage()
            }
        }

        // Error
        viewModel.error?.let { error ->
            Div(AdminErrorStyle.toAttrs()) { Text("❌ $error") }
        }

        // Loading
        if (viewModel.isLoading && viewModel.suggestions.isEmpty()) {
            SpanText(
                "Loading suggestions...",
                Modifier.color(Color.rgb(0xB6, 0x9B, 0x8C))
            )
        } else if (viewModel.suggestions.isEmpty()) {
            SpanText(
                "No suggestions found.",
                Modifier.color(Color.rgb(0xB6, 0x9B, 0x8C))
            )
        } else {
            // List
            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                viewModel.suggestions.forEach { suggestion ->
                    SuggestionCard(
                        suggestion = suggestion,
                        isCheckingWord = viewModel.checkingWordId == suggestion.id,
                        onApprove = { viewModel.onApproveClick(suggestion) },
                        onReject = { viewModel.updateStatus(suggestion.id, "rejected") },
                        onDelete = { viewModel.deleteSuggestion(suggestion.id) }
                    )
                }
            }

            // Pagination
            val totalPages = ((viewModel.totalSuggestions + 19) / 20).toInt()
            if (totalPages > 1) {
                Row(
                    Modifier.fillMaxWidth()
                        .justifyContent(JustifyContent.Center)
                        .gap(0.75.cssRem)
                        .margin(top = 1.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (viewModel.currentPage > 1) {
                        Div(
                            AdminButtonStyle.toAttrs {
                                onClick { viewModel.loadSuggestions(viewModel.currentPage - 1) }
                            }
                        ) { Text("← Previous") }
                    }

                    SpanText(
                        "Page ${viewModel.currentPage} of $totalPages",
                        Modifier.padding(leftRight = 1.cssRem).fontWeight(FontWeight.Medium)
                    )

                    if (viewModel.currentPage < totalPages) {
                        Div(
                            AdminButtonStyle.toAttrs {
                                onClick { viewModel.loadSuggestions(viewModel.currentPage + 1) }
                            }
                        ) { Text("Next →") }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterButton(
    label: String,
    isActive: Boolean,
    onClick: () -> Unit
) {
    val style = if (isActive) ActiveTabStyle else InactiveTabStyle
    Div(style.toAttrs { onClick { onClick() } }) {
        Text(label)
    }
}

/** Small icon button next to the suggestions count — reloads without needing a full page refresh. */
@Composable
private fun RefreshButton(isLoading: Boolean, onClick: () -> Unit) {
    Div(
        Modifier
            .padding(0.25.cssRem, 0.55.cssRem)
            .backgroundColor(Color.rgb(0xF8, 0xDD, 0xC8))
            .color(Color.rgb(0x21, 0x00, 0x00))
            .borderRadius(999.px)
            .fontSize(0.85.cssRem)
            .cursor(Cursor.Pointer)
            .opacity(if (isLoading) 0.5 else 1.0)
            .toAttrs {
                attr("aria-label", "Refresh suggestions")
                attr("title", "Refresh suggestions")
                onClick { if (!isLoading) onClick() }
            }
    ) {
        Text(if (isLoading) "⏳" else "🔄")
    }
}

@Composable
private fun SuggestionCard(
    suggestion: SuggestionItem,
    isCheckingWord: Boolean,
    onApprove: () -> Unit,
    onReject: () -> Unit,
    onDelete: () -> Unit
) {
    var tokenCopied by remember { mutableStateOf(false) }
    val localScope = rememberCoroutineScope()

    Column(SuggestionCardStyle.toModifier()) {
        Row(
            Modifier.fillMaxWidth()
                .justifyContent(JustifyContent.SpaceBetween)
                .flexWrap(FlexWrap.Wrap)
                .gap(0.5.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                suggestion.suggestedWord,
                Modifier.fontSize(1.3.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(Color.rgb(0xD3, 0x35, 0x3E))
            )

            Row(Modifier.gap(0.4.cssRem), verticalAlignment = Alignment.CenterVertically) {
                FcmTokenBadge(suggestion.hasFcmToken)
                StatusBadge(suggestion.status)
            }
        }

        suggestion.userNote?.let { note ->
            SpanText(
                "📝 Note: $note",
                Modifier.fontSize(0.9.cssRem)
                    .color(Color.rgb(0x21, 0x00, 0x00))
                    .margin(top = 0.5.cssRem)
                    .overflowWrap(OverflowWrap.BreakWord)
            )
        }

        Div(
            Modifier.fillMaxWidth()
                .margin(top = 0.75.cssRem)
                .backgroundColor(Color.rgb(0xF8, 0xDD, 0xC8))
                .borderRadius(8.px)
                .padding(0.75.cssRem)
                .toAttrs()
        ) {
            Column(Modifier.gap(0.25.cssRem)) {
                SpanText(
                    "📱 ${suggestion.deviceInfo.platform} • ${suggestion.deviceInfo.osVersion ?: "Unknown OS"}",
                    Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                )
                suggestion.deviceInfo.deviceModel?.let {
                    SpanText(
                        "Device: $it${suggestion.deviceInfo.manufacturer?.let { m -> " ($m)" } ?: ""}",
                        Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                    )
                }
                SpanText(
                    "📦 App v${suggestion.appVersion.versionName} (${suggestion.appVersion.versionCode})",
                    Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                )
                SpanText(
                    "🕐 ${formatTimestamp(suggestion.createdAt)}",
                    Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                )
                suggestion.deviceInfo.language?.let {
                    SpanText(
                        "🌐 Language: $it",
                        Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                    )
                }
                when {
                    suggestion.fcmToken != null -> {
                        Row(
                            Modifier.gap(0.5.cssRem).flexWrap(FlexWrap.Wrap),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                "🔑 ${truncateFcmToken(suggestion.fcmToken)}",
                                Modifier.fontSize(0.78.cssRem)
                                    .color(Color.rgb(0x21, 0x00, 0x00))
                                    .overflowWrap(OverflowWrap.BreakWord)
                            )
                            Div(
                                Modifier.padding(0.1.cssRem, 0.55.cssRem)
                                    .backgroundColor(Colors.White)
                                    .color(Color.rgb(0x19, 0x76, 0xD2))
                                    .border(1.px, LineStyle.Solid, Color.rgba(0x19, 0x76, 0xD2, 0.3f))
                                    .borderRadius(999.px)
                                    .fontSize(0.7.cssRem)
                                    .cursor(Cursor.Pointer)
                                    .toAttrs {
                                        onClick {
                                            localScope.launch {
                                                if (writeClipboardText(suggestion.fcmToken)) {
                                                    tokenCopied = true
                                                }
                                            }
                                        }
                                    }
                            ) { Text(if (tokenCopied) "✓ Copied" else "Copy") }
                        }
                    }
                    suggestion.hasFcmToken -> SpanText(
                        "🔔 Submitter will be notified on approval",
                        Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                    )
                    else -> SpanText(
                        "🔕 No push token attached — approving won't notify anyone",
                        Modifier.fontSize(0.8.cssRem).color(Color.rgb(0x21, 0x00, 0x00))
                    )
                }
            }
        }

        Row(
            Modifier.fillMaxWidth()
                .margin(top = 0.75.cssRem)
                .justifyContent(JustifyContent.FlexEnd)
                .gap(0.5.cssRem)
                .flexWrap(FlexWrap.Wrap)
        ) {
            if (suggestion.status != "approved") {
                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0x2E, 0x7D, 0x32))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { if (!isCheckingWord) onApprove() } }
                ) { Text(if (isCheckingWord) "⏳ Checking…" else "✓ Approve") }
            }

            if (suggestion.status != "rejected") {
                Div(
                    Modifier.padding(0.5.cssRem, 1.cssRem)
                        .backgroundColor(Color.rgb(0xC6, 0x28, 0x28))
                        .color(Colors.White)
                        .borderRadius(8.px)
                        .fontSize(0.85.cssRem)
                        .cursor(Cursor.Pointer)
                        .toAttrs { onClick { onReject() } }
                ) { Text("✗ Reject") }
            }

            Div(
                Modifier.padding(0.5.cssRem, 1.cssRem)
                    .backgroundColor(Color.rgb(0x75, 0x75, 0x75))
                    .color(Colors.White)
                    .borderRadius(8.px)
                    .fontSize(0.85.cssRem)
                    .cursor(Cursor.Pointer)
                    .toAttrs { onClick { onDelete() } }
            ) { Text("🗑 Delete") }
        }
    }
}

@Composable
private fun StatusBadge(status: String) {
    val (bgColor, textColor) = when (status) {
        "pending" -> Color.rgb(0xFF, 0xC1, 0x07) to Color.rgb(0x21, 0x00, 0x00)
        "approved" -> Color.rgb(0x2E, 0x7D, 0x32) to Colors.White
        "rejected" -> Color.rgb(0xC6, 0x28, 0x28) to Colors.White
        "reviewed" -> Color.rgb(0x19, 0x76, 0xD2) to Colors.White
        else -> Color.rgb(0x75, 0x75, 0x75) to Colors.White
    }

    Div(
        Modifier.padding(0.25.cssRem, 0.75.cssRem)
            .backgroundColor(bgColor)
            .color(textColor)
            .borderRadius(20.px)
            .fontSize(0.75.cssRem)
            .fontWeight(FontWeight.Bold)
            .toAttrs()  // ✅ ADD THIS
    ) {
        Text(status.uppercase())
    }
}

/** Small pill next to the status badge showing whether approving this suggestion will fire a push. */
@Composable
private fun FcmTokenBadge(hasFcmToken: Boolean) {
    val (bgColor, textColor, label) = if (hasFcmToken) {
        Triple(Color.rgb(0x19, 0x76, 0xD2), Colors.White, "🔔")
    } else {
        Triple(Color.rgb(0xE0, 0xE0, 0xE0), Color.rgb(0x75, 0x75, 0x75), "🔕")
    }

    Div(
        Modifier.padding(0.25.cssRem, 0.6.cssRem)
            .backgroundColor(bgColor)
            .color(textColor)
            .borderRadius(20.px)
            .fontSize(0.75.cssRem)
            .fontWeight(FontWeight.Bold)
            .toAttrs()
    ) {
        Text(label)
    }
}

/** Shows enough of the token to eyeball/compare without dumping the full 150+ char string inline. */
private fun truncateFcmToken(token: String): String =
    if (token.length <= 22) token else "${token.take(14)}…${token.takeLast(6)}"

private fun formatTimestamp(millis: Long): String {
    val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(millis)
    val localDateTime = instant.toLocalDateTime(kotlinx.datetime.TimeZone.currentSystemDefault())
    return "${localDateTime.date} ${localDateTime.time.toString().substringBefore(".")}"
}